'use client';

import { MetaProps, PageType } from '@benzinga/seo';
import { runningClientSide } from '@benzinga/utils';

const PTYPE_TO_PAGE_TYPE: Record<string, PageType> = {
  calendar: PageType.Calendar,
  channel: PageType.Channel,
  front: PageType.Front,
  premarket: PageType.Website,
  podcasts: PageType.Channel,
  quiz: PageType.Tool,
  quiz_leaderboard: PageType.Tool,
  quiz_rules: PageType.Tool,
  tool: PageType.Tool,
  website: PageType.Website,
};

function readMetaDescription(): string {
  if (!runningClientSide()) return '';
  return (
    document.querySelector('meta[name="description"]')?.getAttribute('content')?.trim() ??
    document.querySelector('meta[property="og:description"]')?.getAttribute('content')?.trim() ??
    ''
  );
}

function readTitle(): string {
  if (!runningClientSide()) return '';
  return (
    document.querySelector('meta[property="og:title"]')?.getAttribute('content')?.trim() ??
    document.title
  );
}

function normalizePath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

function readCanonical(pathname: string): string {
  if (!runningClientSide()) return `https://www.benzinga.com${pathname}`;

  const canonicalHref = document.querySelector('link[rel="canonical"]')?.getAttribute('href')?.trim();
  if (!canonicalHref) {
    return `https://www.benzinga.com${pathname}`;
  }

  try {
    const canonicalPath = normalizePath(new URL(canonicalHref, window.location.origin).pathname);
    const normalizedPathname = normalizePath(pathname);

    if (canonicalPath === '/' && normalizedPathname !== '/') {
      return `${window.location.origin}${normalizedPathname}`;
    }
  } catch {
    return canonicalHref;
  }

  return canonicalHref;
}

export function buildClientRouteMeta(
  pathname: string,
  pageTargeting?: { BZ_PTYPE?: string; BZ_CHANNEL?: string | string[] },
): MetaProps {
  const bzPageType = pageTargeting?.BZ_PTYPE;
  const mappedPageType = bzPageType ? PTYPE_TO_PAGE_TYPE[bzPageType] : undefined;

  return {
    canonical: readCanonical(pathname),
    description: readMetaDescription(),
    pageType: mappedPageType,
    title: readTitle(),
  };
}
